import { useCallback, useEffect, useRef } from 'react';

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * La línea de luz que une la burbuja con el chat y después rodea el panel.
 * Geometría y animación portadas tal cual del prototipo chat-2-sml.html.
 */
export const useLmxTether = ({ layerRef, orbRef, panelRef, tetherRef, open }) => {
  const raf = useRef(0);
  const glowT = useRef(null);

  const paths = useCallback(() => {
    const svg = tetherRef.current;
    if (!svg) return null;
    const q = (c) => svg.querySelector(c);
    return {
      glow: q('.lmx-t-glow'), core: q('.lmx-t-core'), core2: q('.lmx-t-core2'),
      rglow: q('.lmx-t-rglow'), rcore: q('.lmx-t-rcore'),
      sA: q('.lmx-t-sa'), sB: q('.lmx-t-sb'),
      sAh: q('.lmx-t-sah'), sBh: q('.lmx-t-sbh'),
    };
  }, [tetherRef]);

  const setD = useCallback(() => {
    const layer = layerRef.current; const orb = orbRef.current; const panel = panelRef.current;
    const T = paths();
    if (!layer || !orb || !panel || !T) return;
    const L = layer.getBoundingClientRect(); const P = panel.getBoundingClientRect();
    const w = orb.offsetWidth;
    const ox = orb.offsetLeft + w / 2; const oy = orb.offsetTop + w / 2;
    const sx = ox + w * 0.46; const sy = oy + w * 0.08;
    const x = P.left - L.left; const y = P.top - L.top;
    const W = P.width; const H = P.height; const yb = y + H; const r = 14;
    const ex = Math.min(Math.max(x + W * 0.3, sx + w * 1.4), x + W - r - 10);
    const dx = ex - sx;
    const line = `M${sx},${sy} C${sx + dx * 0.35},${sy + 64} ${ex - dx * 0.28},${yb + 58} ${ex},${yb}`;
    const line2 = `M${sx},${sy - 4} C${sx + dx * 0.4},${sy + 40} ${ex - dx * 0.32},${yb + 76} ${ex + 10},${yb}`;
    const cw = `L${x + r},${yb} A${r},${r} 0 0 1 ${x},${yb - r} L${x},${y + r} A${r},${r} 0 0 1 ${x + r},${y} L${x + W - r},${y} A${r},${r} 0 0 1 ${x + W},${y + r} L${x + W},${yb - r} A${r},${r} 0 0 1 ${x + W - r},${yb} L${ex},${yb}`;
    const ccw = `L${x + W - r},${yb} A${r},${r} 0 0 0 ${x + W},${yb - r} L${x + W},${y + r} A${r},${r} 0 0 0 ${x + W - r},${y} L${x + r},${y} A${r},${r} 0 0 0 ${x},${y + r} L${x},${yb - r} A${r},${r} 0 0 0 ${x + r},${yb} L${ex},${yb}`;
    [T.glow, T.core].forEach((p) => p?.setAttribute('d', line));
    T.core2?.setAttribute('d', line2);
    [T.rglow, T.rcore].forEach((p) => p?.setAttribute('d', `M${ex},${yb} ${cw}`));
    [T.sA, T.sAh].forEach((p) => p?.setAttribute('d', `${line} ${cw}`));
    [T.sB, T.sBh].forEach((p) => p?.setAttribute('d', `${line} ${ccw}`));
  }, [layerRef, orbRef, panelRef, paths]);

  const stopSparks = useCallback(() => {
    cancelAnimationFrame(raf.current); raf.current = 0;
    const T = paths();
    [T?.sA, T?.sB, T?.sAh, T?.sBh].forEach((p) => { if (p) p.style.opacity = 0; });
  }, [paths]);

  /* dos destellos salen de la burbuja, recorren la línea y rodean el panel */
  const startSparks = useCallback((delay) => {
    stopSparks();
    if (delay < 0) return;
    const T = paths();
    if (!T?.sA || !T?.sB) return;
    const lenA = T.sA.getTotalLength(); const lenB = T.sB.getTotalLength();
    const parts = [[T.sA, lenA, 44, 0.85], [T.sB, lenB, 44, 0.85],
      [T.sAh, lenA, 110, 0.4], [T.sBh, lenB, 110, 0.4]];
    parts.forEach(([p, l, s]) => {
      p.style.strokeDasharray = `${s} ${l + s}`;
      p.style.strokeDashoffset = s;
    });
    let first = true; let start = performance.now() + delay; let dur = 0; let level = 1;
    const tick = (now) => {
      if (!dur) {
        if (now >= start) {
          dur = (Math.max(lenA, lenB) / (first ? 300 : 150)) * 1000;
          level = first ? 1 : 0.45; start = now;
        }
        parts.forEach(([p]) => { p.style.opacity = 0; });
      } else {
        const k = (now - start) / dur;
        if (k >= 1) {
          dur = 0; first = false; start = now + 14000;
          parts.forEach(([p]) => { p.style.opacity = 0; });
        } else {
          parts.forEach(([p, l, s, o]) => {
            p.style.strokeDashoffset = s - k * (l + s);
            p.style.opacity = (k < 0.06 ? k / 0.06 : k > 0.85 ? (1 - k) / 0.15 : 1) * o * level;
          });
        }
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, [paths, stopSparks]);

  const show = useCallback((animate) => {
    const svg = tetherRef.current; const layer = layerRef.current; const panel = panelRef.current;
    if (!svg || !layer || !panel) return;
    setD();
    svg.classList.add('is-on');
    layer.style.setProperty('--lmx-ph', `${panel.offsetHeight}px`);
    clearTimeout(glowT.current);
    const soft = reduced();
    if (animate && !soft) {
      glowT.current = setTimeout(() => layer.classList.add('is-glow'), 1500);
    } else {
      layer.classList.add('is-glow');
    }
    const T = paths();
    const all = [T.glow, T.core, T.core2, T.rglow, T.rcore].filter(Boolean);
    const prime = (p, len, shown) => {
      p.style.transition = 'none';
      p.style.strokeDasharray = len;
      p.style.strokeDashoffset = shown ? 0 : len;
    };
    if (animate && !soft) {
      const draw = (p, ms, delay) => {
        const len = p.getTotalLength();
        prime(p, len, false);
        p.getBoundingClientRect();
        p.style.transition = `stroke-dashoffset ${ms}ms cubic-bezier(.4,0,.2,1) ${delay}ms`;
        p.style.strokeDashoffset = 0;
      };
      [T.glow, T.core, T.core2].forEach((p) => draw(p, 900, 0));
      [T.rglow, T.rcore].forEach((p) => draw(p, 1100, 750));
      startSparks(1900);
    } else {
      all.forEach((p) => prime(p, p.getTotalLength(), true));
      startSparks(soft ? -1 : 0);
    }
  }, [layerRef, panelRef, paths, setD, startSparks, tetherRef]);

  const hide = useCallback(() => {
    const svg = tetherRef.current; const layer = layerRef.current;
    svg?.classList.remove('is-on');
    layer?.classList.remove('is-glow');
    clearTimeout(glowT.current);
    stopSparks();
  }, [layerRef, stopSparks, tetherRef]);

  /* la línea aparece recién cuando la burbuja llegó a su esquina */
  useEffect(() => {
    if (!open) { hide(); return undefined; }
    const t = setTimeout(() => show(true), reduced() ? 0 : 820);
    return () => clearTimeout(t);
  }, [open, show, hide]);

  useEffect(() => {
    if (!open) return undefined;
    let t;
    const onResize = () => { clearTimeout(t); t = setTimeout(() => show(false), 120); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t); };
  }, [open, show]);

  useEffect(() => () => { cancelAnimationFrame(raf.current); clearTimeout(glowT.current); }, []);

  return { redraw: setD };
};

export default useLmxTether;
